import React, { createContext, useContext, ReactNode, useRef } from "react";
import {
  SnackbarProvider as NotistackProvider,
  useSnackbar as useNotistackSnackbar,
  VariantType,
} from "notistack";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

type SnackbarContextType = {
  showSnackbar: (
    message: string | React.ReactNode,
    severity?: "success" | "error" | "warning" | "info",
    options?: {
      anchorOrigin?: {
        vertical: "top";
        horizontal: "center";
      };
      onClose?: () => void;
      action?: React.ReactNode;
      autoHideDuration?: null | number;
    }
  ) => void;
};

const SnackbarContext = createContext<SnackbarContextType | undefined>(
  undefined
);

export const SnackbarProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { enqueueSnackbar, closeSnackbar } = useNotistackSnackbar();
  const isFirstSnackbar = useRef(true);

  const showSnackbar = (
    message: string | React.ReactNode,
    severity: "success" | "error" | "warning" | "info" = "info",
    options?: {
      anchorOrigin?: { vertical: "top"; horizontal: "center" };
      onClose?: () => void;
      action?: React.ReactNode;
      autoHideDuration?: null | number;
    }
  ) => {
    const autoHideDuration = 5000;
    isFirstSnackbar.current = false;

    const key = enqueueSnackbar(message, {
      variant: severity as VariantType,
      autoHideDuration,
      anchorOrigin: { vertical: "top", horizontal: "center" },
      action: (dismissKey) => (
        <IconButton
          size="small"
          color="inherit"
          onClick={() => {
            closeSnackbar(dismissKey);
          }}
        >
          <CloseIcon />
        </IconButton>
      ),
      style: {
        whiteSpace: "pre-line",
      },
      ...options,
      onClose: options?.onClose,
    });
  };

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error("useSnackbar must be used within a SnackbarProvider");
  }
  return context;
};
